import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { format } from "date-fns";
import axios from "axios";
import {
  CalendarDays, Clock3, Users, CheckCircle2, ArrowLeft, ArrowRight,
  AlertTriangle, BadgeCheck, Printer, Info, ChevronsUpDown, Check, Loader2, ImageUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "../components/ui/command";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { TESTS, ADDITIONAL_TESTS, COUNTRIES, REGISTRATION_INTRO, SITE } from "../mock";
import { PayOnlineCard } from "../components/PayOnline";

const TEST_META = [...TESTS, ...ADDITIONAL_TESTS];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyTestee = () => ({
  fullName: "", dobDate: null, nativeCountry: "", workerRef: "",
  takenBefore: "No", workPermit: "Yes", email: "", photoDataUrl: null, photoFilename: "",
});

const StepDot = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2.5">
    <div
      className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-extrabold transition-colors ${
        done ? "bg-[#0E9AAA] text-white" : active ? "bg-[#0A2540] text-white" : "bg-slate-100 text-slate-400"
      }`}
    >
      {done ? <CheckCircle2 size={18} /> : n}
    </div>
    <span className={`text-sm font-bold hidden sm:block ${active || done ? "text-[#0A2540]" : "text-slate-400"}`}>{label}</span>
  </div>
);

// Searchable country combobox (cmdk)
const CountryCombobox = ({ value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          data-testid={testId}
          className="mt-2 w-full justify-between rounded-lg font-normal text-left hover:bg-white"
        >
          <span className={value ? "text-[#0A2540]" : "text-slate-400"}>
            {value || "Search & select country..."}
          </span>
          <ChevronsUpDown size={15} className="text-slate-400 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Type to search countries..." />
          <CommandList className="max-h-60">
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map((c) => (
                <CommandItem
                  key={c}
                  value={c}
                  onSelect={() => { onChange(c); setOpen(false); }}
                >
                  <Check size={15} className={`mr-2 ${value === c ? "opacity-100 text-[#0E9AAA]" : "opacity-0"}`} />
                  {c}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// DOB calendar picker with year/month dropdowns
export const DobPicker = ({ value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid={testId}
          className="mt-2 w-full justify-between rounded-lg font-normal hover:bg-white"
        >
          <span className={value ? "text-[#0A2540]" : "text-slate-400"}>
            {value ? format(value, "MM/dd/yyyy") : "mm/dd/yyyy"}
          </span>
          <CalendarDays size={15} className="text-slate-400 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={(d) => { onChange(d); setOpen(false); }}
          captionLayout="dropdown-buttons"
          fromYear={1930}
          toYear={new Date().getFullYear()}
          defaultMonth={value || new Date(1990, 0)}
          disabled={(d) => d > new Date()}
          classNames={{
            caption_dropdowns: "flex gap-1.5",
            dropdown: "rounded-md border border-slate-200 bg-white px-1.5 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#0E9AAA]",
            caption_label: "hidden",
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default function Booking() {
  const [searchParams] = useSearchParams();
  const preselect = searchParams.get("test");
  const [step, setStep] = useState(preselect ? 2 : 1);
  const [testId, setTestId] = useState(preselect || "");
  const [tests, setTests] = useState([]);
  const [date, setDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [employer, setEmployer] = useState({ name: "", contact: "", email: "" });
  const [testees, setTestees] = useState([emptyTestee()]);
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const dateISO = date ? format(date, "yyyy-MM-dd") : null;

  // Merge backend tests (authoritative prices) with local display metadata
  useEffect(() => {
    axios
      .get(`${API}/tests`)
      .then((res) => {
        const merged = res.data.map((bt) => {
          const meta = TEST_META.find((m) => m.id === bt.id) || {};
          return { ...meta, ...bt, shortName: bt.short_name || meta.shortName };
        });
        setTests(merged);
      })
      .catch(() => {
        toast.error("Could not load tests. Please refresh the page.");
        setTests(TEST_META);
      });
  }, []);

  useEffect(() => {
    setTestees((prev) => {
      const next = [...prev];
      while (next.length < quantity) next.push(emptyTestee());
      return next.slice(0, quantity);
    });
  }, [quantity]);

  // Fetch availability whenever date changes
  useEffect(() => {
    if (!dateISO || !testId) { setSlots([]); return; }
    setLoadingSlots(true);
    axios
      .get(`${API}/availability`, { params: { test_id: testId, date: dateISO } })
      .then((res) => setSlots(res.data.slots))
      .catch(() => toast.error("Could not load availability."))
      .finally(() => setLoadingSlots(false));
  }, [dateISO, testId]);

  const test = tests.find((t) => t.id === testId) || TEST_META.find((t) => t.id === testId);
  const selectedSlotSeats = slots.find((s) => s.time === timeSlot)?.seats_remaining ?? 10;
  const isPublicTest = Boolean(test?.requires_photo);
  const cutoffDays = test?.cutoff_days || 1;

  // Blackout dates (no testing available)
  const [blackouts, setBlackouts] = useState(new Set());
  useEffect(() => {
    axios
      .get(`${API}/blackouts`)
      .then((res) => setBlackouts(new Set(res.data.dates)))
      .catch(() => {});
  }, []);

  const disabledDays = (d) => {
    // Per-test day rules + booking cutoff (Cayman time)
    const nowCayman = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Cayman" }));
    const minDate = new Date(nowCayman);
    minDate.setHours(0, 0, 0, 0);
    if (cutoffDays > 1) {
      minDate.setDate(minDate.getDate() + cutoffDays);
    } else {
      minDate.setDate(minDate.getDate() + (nowCayman.getHours() >= 19 ? 2 : 1));
    }
    if (d < minDate) return true;
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (blackouts.has(iso)) return true;
    if (test?.days_rule === "second_tuesday") {
      return !(d.getDay() === 2 && d.getDate() >= 8 && d.getDate() <= 14);
    }
    const jsDays = test?.js_days || [1, 2, 3, 4, 5];
    return !jsDays.includes(d.getDay());
  };

  const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const validateForm = () => {
    if (!validEmail(employer.email)) {
      toast.error("Please enter a valid Employer/Company Email.");
      return false;
    }
    for (let i = 0; i < testees.length; i++) {
      const t = testees[i];
      if (!t.fullName.trim()) { toast.error(`Testee ${i + 1}: Full Name is required.`); return false; }
      if (!t.dobDate) { toast.error(`Testee ${i + 1}: Please select a Date of Birth.`); return false; }
      if (!t.nativeCountry) { toast.error(`Testee ${i + 1}: Native Country is required.`); return false; }
      if (isPublicTest) {
        if (!validEmail(t.email)) { toast.error(`Testee ${i + 1}: a valid Email Address is required.`); return false; }
        if (!t.photoDataUrl) { toast.error(`Testee ${i + 1}: please upload a head shot photo.`); return false; }
      } else {
        if (!t.workerRef.trim()) { toast.error(`Testee ${i + 1}: Worker Reference # is required.`); return false; }
      }
    }
    return true;
  };

  const handlePhotoUpload = (i, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (JPG or PNG).");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Photo is too large. Please upload an image under 2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateTestee(i, "photoDataUrl", reader.result);
      updateTestee(i, "photoFilename", file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!validateForm() || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        test_id: testId,
        date: dateISO,
        time_slot: timeSlot,
        quantity,
        employer: { name: employer.name, contact: employer.contact, email: employer.email },
        testees: testees.map((t) => ({
          full_name: t.fullName,
          dob: format(t.dobDate, "MM/dd/yyyy"),
          native_country: t.nativeCountry,
          worker_ref: t.workerRef,
          taken_before: t.takenBefore,
          work_permit: t.workPermit,
          email: t.email,
          photo_base64: isPublicTest ? t.photoDataUrl : null,
          photo_filename: t.photoFilename || "",
        })),
      };
      const res = await axios.post(`${API}/bookings`, payload);
      setConfirmation(res.data);
      setStep(4);
      toast.success("Your test has been scheduled!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not complete your booking. Please check your information and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const updateTestee = (i, field, value) =>
    setTestees((prev) => prev.map((t, idx) => (idx === i ? { ...t, [field]: value } : t)));

  const displayTests = tests.length ? tests : TEST_META;

  return (
    <div className="bg-[#F6FAFB] min-h-screen" data-testid="booking-page">
      {/* Page header */}
      <div className="bg-[#0A2540] py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Test Registration</h1>
          <p className="mt-3 text-slate-300 max-w-3xl mx-auto text-sm sm:text-base leading-relaxed">{REGISTRATION_INTRO}</p>
          <p className="mt-4 text-[#39C1D0] font-bold text-sm uppercase tracking-wide">
            To schedule a test in the Sister Islands please email {SITE.sisterIslandsEmail}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {/* Steps */}
        <div className="flex items-center justify-between mb-10 bg-white rounded-2xl border border-slate-100 shadow-sm px-6 py-4">
          <StepDot n={1} label="Select Test" active={step === 1} done={step > 1} />
          <div className="flex-1 h-px bg-slate-200 mx-3" />
          <StepDot n={2} label="Date & Time" active={step === 2} done={step > 2} />
          <div className="flex-1 h-px bg-slate-200 mx-3" />
          <StepDot n={3} label="Registration" active={step === 3} done={step > 3} />
          <div className="flex-1 h-px bg-slate-200 mx-3" />
          <StepDot n={4} label="Confirmation" active={step === 4} done={false} />
        </div>

        {/* STEP 1 - Select test */}
        {step === 1 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="step-select-test">
            {displayTests.map((t) => (
              <Card
                key={t.id}
                data-testid={`select-test-${t.id}`}
                onClick={() => { setTestId(t.id); setDate(null); setTimeSlot(""); setStep(2); }}
                className={`rounded-2xl cursor-pointer border-2 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                  testId === t.id ? "border-[#0E9AAA]" : "border-transparent shadow-sm"
                }`}
              >
                <CardContent className="p-7">
                  <Badge className="bg-[#E8F7F9] text-[#0C8695] hover:bg-[#E8F7F9] border border-[#B5E6EC] font-semibold mb-4">{t.badge}</Badge>
                  <h3 className="font-extrabold text-[#0A2540] leading-snug">{t.shortName}</h3>
                  <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{t.description}</p>
                  <div className="mt-4 text-sm text-slate-500 flex items-center gap-2"><Clock3 size={14} className="text-[#0E9AAA]" /> {t.schedule}</div>
                  <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <span className="font-extrabold text-[#0A2540] text-lg">CI ${Number(t.price).toFixed(2)}</span>
                    <span className="text-sm font-bold text-[#0E9AAA] flex items-center gap-1">Select <ArrowRight size={14} /></span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* STEP 2 - Date & time */}
        {step === 2 && test && (
          <div data-testid="step-date-time">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-extrabold text-[#0A2540]">{test.name}</h2>
                <p className="text-sm text-slate-500 mt-1">{test.schedule} • CI ${Number(test.price).toFixed(2)} per test</p>
              </div>
              <Button variant="ghost" onClick={() => setStep(1)} className="text-slate-500 font-semibold">
                <ArrowLeft size={16} className="mr-1.5" /> Change Test
              </Button>
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="rounded-2xl border-slate-100 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 font-bold text-[#0A2540] mb-4">
                    <CalendarDays size={18} className="text-[#0E9AAA]" /> Select a Date
                  </div>
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => { setDate(d); setTimeSlot(""); }}
                    disabled={disabledDays}
                    className="rounded-xl border border-slate-100 mx-auto"
                  />
                  <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5">
                    <Info size={13} /> {test.schedule}. {cutoffDays > 1
                      ? `Booking closes ${cutoffDays} days before the test date.`
                      : "Booking closes at 7:00 PM the day before the test."}
                  </p>
                </CardContent>
              </Card>
              <div className="space-y-6">
                <Card className="rounded-2xl border-slate-100 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 font-bold text-[#0A2540] mb-4">
                      <Clock3 size={18} className="text-[#0E9AAA]" /> Select a Time
                    </div>
                    {!date ? (
                      <p className="text-sm text-slate-400">Please select a date first.</p>
                    ) : loadingSlots ? (
                      <div className="flex items-center gap-2 text-sm text-slate-400 py-3">
                        <Loader2 size={16} className="animate-spin" /> Checking availability...
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        {slots.map((slot) => {
                          const full = slot.seats_remaining <= 0;
                          return (
                            <button
                              key={slot.time}
                              data-testid={`slot-${slot.time.replace(/[: ]/g, "")}`}
                              disabled={full}
                              onClick={() => { setTimeSlot(slot.time); setQuantity(1); }}
                              className={`rounded-xl border-2 p-4 text-left transition-colors ${
                                full
                                  ? "border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed"
                                  : timeSlot === slot.time
                                  ? "border-[#0E9AAA] bg-[#E8F7F9]"
                                  : "border-slate-200 hover:border-[#0E9AAA]"
                              }`}
                            >
                              <div className="font-extrabold text-[#0A2540]">{slot.time}</div>
                              <div className={`text-xs mt-1 font-semibold ${full ? "text-red-500" : "text-slate-500"}`}>
                                {full ? "Fully Booked" : `${slot.seats_remaining} seat${slot.seats_remaining === 1 ? "" : "s"} remaining`}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card className="rounded-2xl border-slate-100 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 font-bold text-[#0A2540] mb-4">
                      <Users size={18} className="text-[#0E9AAA]" /> Quantity (Number of Testees)
                    </div>
                    <Select value={String(quantity)} onValueChange={(v) => setQuantity(Number(v))}>
                      <SelectTrigger data-testid="quantity-select" className="w-full rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: Math.max(1, timeSlot ? selectedSlotSeats : 10) }, (_, i) => i + 1).map((n) => (
                          <SelectItem key={n} value={String(n)}>{String(n).padStart(2, "0")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>
                <Button
                  data-testid="continue-to-form-btn"
                  disabled={!date || !timeSlot}
                  onClick={() => setStep(3)}
                  className="w-full bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-xl h-12 text-base shadow-md disabled:opacity-50"
                >
                  Continue to Registration <ArrowRight size={17} className="ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 - Registration form */}
        {step === 3 && test && (
          <div data-testid="step-registration">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-extrabold text-[#0A2540]">Register Below:</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {test.shortName} • {date && format(date, "EEE, MMM d, yyyy")} at {timeSlot} • {quantity} testee{quantity > 1 ? "s" : ""}
                </p>
              </div>
              <Button variant="ghost" onClick={() => setStep(2)} className="text-slate-500 font-semibold">
                <ArrowLeft size={16} className="mr-1.5" /> Back
              </Button>
            </div>

            <Card className="rounded-2xl border-slate-100 shadow-sm mb-6">
              <CardContent className="p-7">
                <div className="text-sm font-extrabold text-[#0E9AAA] uppercase tracking-wide mb-5">
                  Enter Company/Employer Information Below
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="font-bold text-[#0A2540]">Employer/Company Name *</Label>
                    <Input
                      data-testid="employer-name-input"
                      value={employer.name}
                      onChange={(e) => setEmployer({ ...employer, name: e.target.value })}
                      placeholder="Leave Blank if Not Applicable"
                      className="mt-2 rounded-lg"
                    />
                  </div>
                  <div>
                    <Label className="font-bold text-[#0A2540]">Employer Contact Number *</Label>
                    <Input
                      data-testid="employer-contact-input"
                      value={employer.contact}
                      onChange={(e) => setEmployer({ ...employer, contact: e.target.value })}
                      placeholder="Leave Blank if Not Applicable"
                      className="mt-2 rounded-lg"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="font-bold text-[#0A2540]">Employer/Company Email *</Label>
                    <Input
                      data-testid="employer-email-input"
                      type="email"
                      value={employer.email}
                      onChange={(e) => setEmployer({ ...employer, email: e.target.value })}
                      placeholder="email@company.com"
                      className="mt-2 rounded-lg"
                    />
                    <p className="text-xs text-slate-400 mt-1.5">Your booking confirmation will be sent to this email.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {testees.map((t, i) => (
              <Card key={i} className="rounded-2xl border-slate-100 shadow-sm mb-6">
                <CardContent className="p-7">
                  <div className="text-sm font-extrabold text-[#0E9AAA] uppercase tracking-wide">
                    {isPublicTest ? "Enter Your Information Below" : "Enter Testee/Employee Information Below"} {quantity > 1 && `— Testee ${i + 1} of ${quantity}`}
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5 mb-5">
                    Testee must bring 2 forms of Government Identification (including PASSPORT) to Test Center for verification of information.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="sm:col-span-2">
                      <Label className="font-bold text-[#0A2540]">Testee Full Name — AS IT APPEARS IN PASSPORT *</Label>
                      <Input
                        data-testid={`testee-name-input-${i}`}
                        value={t.fullName}
                        onChange={(e) => updateTestee(i, "fullName", e.target.value)}
                        className="mt-2 rounded-lg"
                      />
                    </div>
                    <div>
                      <Label className="font-bold text-[#0A2540]">Date of Birth *</Label>
                      <DobPicker
                        value={t.dobDate}
                        onChange={(d) => updateTestee(i, "dobDate", d)}
                        testId={`testee-dob-picker-${i}`}
                      />
                    </div>
                    <div>
                      <Label className="font-bold text-[#0A2540]">Native Country *</Label>
                      <CountryCombobox
                        value={t.nativeCountry}
                        onChange={(v) => updateTestee(i, "nativeCountry", v)}
                        testId={`testee-country-combobox-${i}`}
                      />
                    </div>
                    {!isPublicTest && (
                    <div className="sm:col-span-2">
                      <Label className="font-bold text-[#0A2540]">Worker Reference # (Located on Work Permit License) *</Label>
                      <Input
                        data-testid={`testee-workerref-input-${i}`}
                        value={t.workerRef}
                        onChange={(e) => updateTestee(i, "workerRef", e.target.value)}
                        className="mt-2 rounded-lg"
                      />
                    </div>
                    )}
                    {isPublicTest && (
                    <>
                    <div>
                      <Label className="font-bold text-[#0A2540]">Email Address *</Label>
                      <Input
                        data-testid={`testee-email-input-${i}`}
                        type="email"
                        value={t.email}
                        onChange={(e) => updateTestee(i, "email", e.target.value)}
                        placeholder="testee@email.com"
                        className="mt-2 rounded-lg"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="font-bold text-[#0A2540]">Head Shot Photo * (192 pixels wide x 240 pixels tall)</Label>
                      <p className="text-xs text-slate-500 mt-1">
                        Photo must have no dark/shadow spots and candidate must be a front profile photo just like a passport photo. The file name should be the same name as the testee.
                      </p>
                      <div className="mt-3 flex items-center gap-4">
                        <label
                          htmlFor={`photo-upload-${i}`}
                          className="cursor-pointer inline-flex items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 hover:border-[#0E9AAA] px-5 py-3 text-sm font-bold text-slate-600 hover:text-[#0E9AAA] transition-colors"
                        >
                          <ImageUp size={17} /> {t.photoDataUrl ? "Change Photo" : "Upload Photo"}
                        </label>
                        <input
                          id={`photo-upload-${i}`}
                          data-testid={`testee-photo-input-${i}`}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handlePhotoUpload(i, e.target.files?.[0])}
                        />
                        {t.photoDataUrl && (
                          <div className="flex items-center gap-3">
                            <img
                              src={t.photoDataUrl}
                              alt={`Head shot preview for testee ${i + 1}`}
                              className="h-20 w-16 object-cover rounded-lg border border-slate-200 shadow-sm"
                            />
                            <span className="text-xs text-slate-500 font-semibold max-w-[160px] truncate">{t.photoFilename}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    </>
                    )}
                    {!isPublicTest && (
                    <>
                    <div>
                      <Label className="font-bold text-[#0A2540]">Have You Taken This Test Before? *</Label>
                      <RadioGroup
                        value={t.takenBefore}
                        onValueChange={(v) => updateTestee(i, "takenBefore", v)}
                        className="flex gap-6 mt-3"
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="No" id={`taken-no-${i}`} />
                          <Label htmlFor={`taken-no-${i}`}>No</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="Yes" id={`taken-yes-${i}`} />
                          <Label htmlFor={`taken-yes-${i}`}>Yes</Label>
                        </div>
                      </RadioGroup>
                    </div>
                    <div>
                      <Label className="font-bold text-[#0A2540]">This test is being taken for a work permit application. *</Label>
                      <RadioGroup
                        value={t.workPermit}
                        onValueChange={(v) => updateTestee(i, "workPermit", v)}
                        className="flex gap-6 mt-3"
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="Yes" id={`wp-yes-${i}`} />
                          <Label htmlFor={`wp-yes-${i}`}>Yes</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="No" id={`wp-no-${i}`} />
                          <Label htmlFor={`wp-no-${i}`}>No</Label>
                        </div>
                      </RadioGroup>
                      {t.workPermit === "Yes" && (
                        <p className="text-xs text-amber-600 font-semibold mt-2.5 flex items-start gap-1.5">
                          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                          If YES please note that a copy of your work permit must also be presented at check in.
                        </p>
                      )}
                    </div>
                    </>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <div>
                <div className="text-sm text-slate-500">Total Cost ({quantity} × CI ${Number(test.price).toFixed(2)})</div>
                <div className="text-2xl font-extrabold text-[#0A2540]">CI ${(test.price * quantity).toFixed(2)}</div>
                <div className="text-xs text-slate-400 mt-1">Payment due on or before check in — cash, debit/credit card or cheque.</div>
              </div>
              <Button
                data-testid="submit-booking-btn"
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-xl px-10 h-12 text-base shadow-md"
              >
                {submitting ? <><Loader2 size={17} className="mr-2 animate-spin" /> Submitting...</> : "Submit"}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4 - Confirmation */}
        {step === 4 && confirmation && (
          <div data-testid="step-confirmation" className="max-w-3xl mx-auto">
            <Card className="rounded-2xl border-slate-100 shadow-lg overflow-hidden">
              <div className="bg-[#0E9AAA] p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                  <BadgeCheck className="text-white" size={34} />
                </div>
                <h2 className="text-2xl font-extrabold text-white">Your Test Has Been Scheduled!</h2>
                <p className="text-white/90 mt-2 text-sm">
                  Confirmation Number: <span className="font-extrabold text-base" data-testid="confirmation-number">{confirmation.confirmation_number}</span>
                </p>
              </div>
              <CardContent className="p-8">
                <div className="grid sm:grid-cols-2 gap-5 text-sm">
                  <div><div className="text-slate-400 font-semibold uppercase text-xs">Test</div><div className="font-bold text-[#0A2540] mt-1">{confirmation.test_name}</div></div>
                  <div><div className="text-slate-400 font-semibold uppercase text-xs">Date & Time</div><div className="font-bold text-[#0A2540] mt-1">{confirmation.date_display} at {confirmation.time_slot}</div></div>
                  <div><div className="text-slate-400 font-semibold uppercase text-xs">Testee(s)</div><div className="font-bold text-[#0A2540] mt-1">{confirmation.testees.map((t) => t.full_name).join(", ")}</div></div>
                  <div><div className="text-slate-400 font-semibold uppercase text-xs">Total Cost</div><div className="font-bold text-[#0A2540] mt-1">CI ${Number(confirmation.total_cost).toFixed(2)}</div></div>
                  <div className="sm:col-span-2"><div className="text-slate-400 font-semibold uppercase text-xs">Location</div><div className="font-bold text-[#0A2540] mt-1">{SITE.address} — {SITE.addressNote}</div></div>
                </div>
                <div className="mt-7 bg-[#F6FAFB] border border-slate-100 rounded-xl p-6">
                  <div className="font-extrabold text-[#0A2540] mb-3">Important Information — Please Read Carefully</div>
                  <ul className="space-y-2.5 text-sm text-slate-600">
                    <li className="flex gap-2.5"><CheckCircle2 size={16} className="text-[#0E9AAA] mt-0.5 shrink-0" /> Payment must be made on or before check in. Accepted forms of payment are cash, debit/credit card and cheques.</li>
                    <li className="flex gap-2.5"><CheckCircle2 size={16} className="text-[#0E9AAA] mt-0.5 shrink-0" /> Test takers are required to arrive no later than 20 minutes before the test start time. Late arrivals will not be allowed to take the test.</li>
                    <li className="flex gap-2.5"><CheckCircle2 size={16} className="text-[#0E9AAA] mt-0.5 shrink-0" /> Each testee must provide 2 forms of identification (one being their passport) when checking in to take the test.</li>
                    <li className="flex gap-2.5"><CheckCircle2 size={16} className="text-[#0E9AAA] mt-0.5 shrink-0" /> If the test is being taken for a work permit application, a copy of the work permit must also be presented at check in.</li>
                    <li className="flex gap-2.5"><CheckCircle2 size={16} className="text-[#0E9AAA] mt-0.5 shrink-0" /> Results will be available in {confirmation.results_text || "two business days"}.</li>
                    {confirmation.policy_note && (
                      <li className="flex gap-2.5"><AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" /> <strong>{confirmation.policy_note}</strong></li>
                    )}
                  </ul>
                  <p className="text-xs text-slate-500 mt-4">
                    Keep your confirmation number safe — you can view or cancel this booking anytime on the{" "}
                    <Link to="/my-booking" className="text-[#0E9AAA] font-bold hover:underline">Find My Booking</Link> page.
                  </p>
                </div>
                <div className="mt-6">
                  <PayOnlineCard
                    confirmationNumber={confirmation.confirmation_number}
                    email={confirmation.employer?.email}
                    totalCost={confirmation.total_cost}
                    paymentStatus={confirmation.payment_status}
                  />
                </div>
                <div className="mt-7 flex flex-wrap gap-4">
                  <Button onClick={() => window.print()} variant="outline" className="border-[#0A2540] text-[#0A2540] font-bold rounded-lg">
                    <Printer size={16} className="mr-2" /> Print Confirmation
                  </Button>
                  <Button
                    data-testid="book-another-btn"
                    onClick={() => {
                      setStep(1); setTestId(""); setDate(null); setTimeSlot("");
                      setQuantity(1); setEmployer({ name: "", contact: "", email: "" });
                      setTestees([emptyTestee()]); setConfirmation(null);
                    }}
                    className="bg-[#0A2540] hover:bg-[#123B63] text-white font-bold rounded-lg"
                  >
                    Book Another Test
                  </Button>
                  <Link to="/"><Button variant="ghost" className="font-bold text-slate-500">Back to Home</Button></Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
